import { useEffect, useRef, useState } from 'react';
import { academyApi, type ModuleWithChapters } from '../../services/academyApi';
import type { AcademyTrack } from '@platform/shared-types';
import CertificationList from './CertificationList';
import { createLoadGuard } from './loadGuard';
import { mergeSavedTrack } from './mergeSavedTrack';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * A bank drawing `questionCount` questions per attempt needs meaningfully more
 * than `questionCount` questions in it, or repeat attempts (a retake after a
 * fail, or two partners sitting the exam back to back) draw largely the same
 * set. 3x the draw size is the bar below which that overlap gets noticeable.
 */
const HEALTHY_BANK_MULTIPLE = 3;

/** Order-independent set equality — module ticks can be toggled in any order. */
function sameModuleSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

/** Defaults for a brand-new certification, before its first save. */
function draftTrack(trackId: string, title: string): AcademyTrack {
  return {
    trackId,
    title,
    moduleIds: [],
    passMarkPercent: 80,
    questionCount: 15,
    shuffleQuestions: true,
    shuffleChoices: true,
    cooldownMinutesAfterFail: 60,
    audience: 'affiliate',
    version: 0,
    published: false,
  };
}

export default function ExamSettingsTab() {
  const [tracks, setTracks] = useState<AcademyTrack[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [track, setTrack] = useState<AcademyTrack | null>(null);
  const [bankSize, setBankSize] = useState(0);
  // The moduleIds set that `bankSize` was actually computed for by the
  // server. `track.moduleIds` changes the instant an admin ticks a
  // checkbox, but `bankSize` only changes when the server recomputes it —
  // so the two can (and do) drift apart. The badge below trusts `bankSize`
  // only while this still matches `track.moduleIds`.
  const [bankSizeFor, setBankSizeFor] = useState<string[]>([]);
  const [modules, setModules] = useState<ModuleWithChapters[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTrack, setLoadingTrack] = useState(false);
  const [saving, setSaving] = useState(false);
  // Governs the CertificationList's own busy state (create/archive in
  // flight) — distinct from `saving`, which is the Publish round trip.
  const [listBusy, setListBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  // One guard for the tab's whole lifetime, wired directly to the
  // `loadingTrack` setter. It now OWNS that flag: `startLoad()` / `beginQuiet()`
  // always decide its new value as part of starting, so no call site can
  // forget to reset it (that hand-wiring is exactly what got stuck in fix
  // round 1 — see loadGuard.ts). Every place that writes `track`, `bankSize`
  // or `bankSizeFor` from an async continuation is threaded through the same
  // guard, so a response for a certification the admin has since clicked
  // away from can never land and silently repopulate the form with the
  // wrong certification's data.
  const loadGuard = useRef(createLoadGuard(setLoadingTrack)).current;

  const loadTrack = async (trackId: string) => {
    const token = loadGuard.startLoad();
    try {
      const t = await academyApi.getTrack(trackId);
      if (!loadGuard.isCurrent(token)) return; // superseded by a newer selection
      setTrack(t.track);
      setBankSize(t.bankSize);
      setBankSizeFor(t.track.moduleIds);
      setError('');
    } catch (e: any) {
      if (!loadGuard.isCurrent(token)) return;
      setTrack(null);
      setError(e.message || 'Could not load this certification.');
    } finally {
      // Settling always runs; the guard itself decides whether this token
      // is still the one allowed to clear `loadingTrack` (a no-op if a
      // newer operation — of either kind — has since started).
      loadGuard.settle(token);
    }
  };

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [{ modules }, { tracks }] = await Promise.all([
          academyApi.listModules(),
          academyApi.listTracks(),
        ]);
        setModules(modules);
        setTracks(tracks);
        const first = tracks.find((t) => t.archived !== true);
        if (first) {
          setSelectedId(first.trackId);
          await loadTrack(first.trackId);
        }
      } catch (e: any) {
        setError(e.message || 'Could not load certifications.');
      } finally {
        setLoading(false);
      }
    })();
    // Runs once on mount — the list of certifications and modules is loaded
    // exactly once; selecting/creating/archiving updates state directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (trackId: string) => {
    if (trackId === selectedId) return;
    setSelectedId(trackId);
    setNotice('');
    void loadTrack(trackId);
  };

  const handleCreate = async (title: string) => {
    const trackId = crypto.randomUUID();
    setListBusy(true);
    try {
      const { track: saved } = await academyApi.saveTrack(trackId, draftTrack(trackId, title));
      // This creation is now the latest thing that gets to decide what's on
      // screen — supersedes any load still in flight from a prior selection
      // (so it can't land afterward and clobber the certification just
      // created) AND clears `loadingTrack` itself, since the data below is
      // already in hand with nothing left to wait on.
      loadGuard.beginQuiet();
      setTracks((prev) => [...prev, saved]);
      setSelectedId(saved.trackId);
      setTrack(saved);
      setBankSize(0);
      setBankSizeFor(saved.moduleIds);
      setNotice('');
      setError('');
    } catch (e: any) {
      setError(e.message || 'Could not create the certification.');
    } finally {
      setListBusy(false);
    }
  };

  const handleArchive = async (trackId: string) => {
    setListBusy(true);
    try {
      await academyApi.archiveTrack(trackId);
      setTracks((prev) => prev.map((t) => (t.trackId === trackId ? { ...t, archived: true } : t)));
      setNotice('Archived. Reps stop seeing it; every pass already on record stays there.');
      setError('');
      if (selectedId === trackId) {
        const next = tracks.find((t) => t.trackId !== trackId && t.archived !== true);
        if (next) {
          setSelectedId(next.trackId);
          await loadTrack(next.trackId);
        } else {
          // Supersedes any load still in flight (e.g. the one for the
          // certification just archived) so it can't land afterward and
          // repopulate `track` right after this clears it, AND clears
          // `loadingTrack` itself — there is nothing left to wait on.
          loadGuard.beginQuiet();
          setSelectedId(null);
          setTrack(null);
        }
      }
    } catch (e: any) {
      // The backend returns 409 for the certification that activates
      // partners — surface its message in-page rather than an unhandled throw.
      setError(e.message || 'Could not archive this certification.');
    } finally {
      setListBusy(false);
    }
  };

  const publish = async () => {
    if (!track || !selectedId || saving || track.moduleIds.length === 0) return;
    // Captured before the network round trip: if the admin selects a
    // different certification while this PUT is in flight, that selection
    // starts its own load and bumps the guard, so this publish's eventual
    // response — for a certification that's no longer the one on screen —
    // is recognised as stale and skipped instead of overwriting what's shown.
    // `beginQuiet` (not `startLoad`): Publish never swaps the pane for a
    // loading message — it keeps the form visible and disables it via
    // `saving` instead — so it must not touch `loadingTrack` either way.
    const token = loadGuard.beginQuiet();
    setSaving(true);
    try {
      const { track: saved } = await academyApi.saveTrack(selectedId, { ...track, published: true });
      // The row itself is refreshed unconditionally (it is keyed by trackId,
      // so it can never land on the wrong row); the exclusivity sweep across
      // the OTHER rows is gated on this publish still being current — see
      // mergeSavedTrack.
      setTracks((prev) => mergeSavedTrack(prev, saved, loadGuard.isCurrent(token)));
      if (loadGuard.isCurrent(token)) {
        setTrack(saved);
        setNotice('Published. New partners now need to pass this exam.');
        setError('');
      }
      // saveTrack's response is `{ track }` only — it does not return an
      // updated bankSize, so without this re-fetch the badge would keep
      // showing whatever was true when the tab first mounted, even though
      // the module selection (and therefore the real bank) may have just
      // changed. If the re-fetch itself fails, deliberately leave
      // `bankSizeFor` untouched: it will then no longer match the just-saved
      // `track.moduleIds`, so the badge falls back to "Unverified" instead
      // of silently reusing a number that may no longer be true.
      try {
        const refreshed = await academyApi.getTrack(selectedId);
        setTracks((prev) => mergeSavedTrack(prev, refreshed.track, loadGuard.isCurrent(token)));
        if (loadGuard.isCurrent(token)) {
          setTrack(refreshed.track);
          setBankSize(refreshed.bankSize);
          setBankSizeFor(refreshed.track.moduleIds);
        }
      } catch {
        // Leave bankSize/bankSizeFor stale-on-purpose — see comment above.
      }
    } catch (e: any) {
      if (loadGuard.isCurrent(token)) setError(e.message || 'Publishing failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <RubyLoader variant="page" label="Loading certifications…" />;

  const activeTracks = tracks.filter((t) => t.archived !== true);
  const noneActivates = activeTracks.length > 0
    && !tracks.some((t) => t.activatesPartner === true && t.published && t.archived !== true);

  const bankTarget = track ? track.questionCount * HEALTHY_BANK_MULTIPLE : 0;
  const healthy = track ? bankSize >= bankTarget : false;
  const shortBy = Math.max(0, bankTarget - bankSize);
  // The server has never counted questions for this exact selection — either
  // the admin has ticked/unticked a module since the last save, or a prior
  // post-publish re-fetch failed. Either way, showing a confident verdict
  // here would be a guess dressed up as a fact.
  const bankVerified = track ? sameModuleSet(track.moduleIds, bankSizeFor) : false;

  return (
    <div className="space-y-4">
      {noneActivates && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          No published certification activates partners yet, so no rep can go live by
          passing an exam. Mark one below.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[280px_1fr] items-start">
        <CertificationList
          tracks={tracks}
          selectedId={selectedId}
          onSelect={handleSelect}
          onCreate={handleCreate}
          onArchive={handleArchive}
          busy={listBusy}
        />

        {!selectedId ? (
          <section className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500">
              Select a certification on the left, or create one, to edit its exam settings.
            </p>
          </section>
        ) : loadingTrack || !track ? (
          <section className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
            {loadingTrack ? (
              <RubyLoader variant="section" label="Loading exam settings…" />
            ) : (
              <p className="text-sm text-gray-500">{error || 'Could not load exam settings.'}</p>
            )}
          </section>
        ) : (
          <div className="grid gap-4 md:grid-cols-[320px_1fr] items-start">
            <section className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-3">Modules required to sit the exam</h2>
              <div className="space-y-3">
                {modules.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No modules exist yet. Add curriculum modules on the Curriculum tab first.
                  </p>
                )}
                {modules.map((m) => (
                  <label key={m.module.moduleId} className="flex items-center justify-between min-h-[44px]">
                    <span className="text-sm text-gray-900">{m.module.title}</span>
                    <input
                      type="checkbox"
                      disabled={saving}
                      className="w-5 h-5 accent-gray-900"
                      checked={track.moduleIds.includes(m.module.moduleId)}
                      onChange={(e) => setTrack({
                        ...track,
                        moduleIds: e.target.checked
                          ? [...track.moduleIds, m.module.moduleId]
                          : track.moduleIds.filter((id) => id !== m.module.moduleId),
                      })}
                    />
                  </label>
                ))}
              </div>
            </section>

            <div className="space-y-4">
              <section className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
                <h2 className="font-semibold text-gray-900 mb-4">Exam rules</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {([
                    ['Pass mark (%)', 'passMarkPercent'],
                    ['Questions drawn', 'questionCount'],
                    ['Cooldown after a fail (min)', 'cooldownMinutesAfterFail'],
                  ] as const).map(([label, key]) => (
                    <label key={key} className="block">
                      <span className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
                      <input
                        type="number"
                        min={0}
                        disabled={saving}
                        value={track[key] as number}
                        onChange={(e) => setTrack({ ...track, [key]: Number(e.target.value) })}
                        className="mt-1 w-full min-h-[44px] bg-white border border-gray-300 rounded-lg px-3 text-gray-900 tabular-nums outline-none focus:border-gray-900 disabled:opacity-50"
                      />
                    </label>
                  ))}
                </div>
              </section>

              <section className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-semibold text-gray-900">Question bank</h2>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    !bankVerified
                      ? 'bg-gray-100 text-gray-600'
                      : healthy ? 'bg-gray-900 text-white' : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}
                  >
                    {!bankVerified ? 'Unverified' : healthy ? 'Healthy' : 'Thin'}
                  </span>
                </div>

                {!bankVerified ? (
                  <p className="text-sm text-gray-400">
                    The question count shown last reflects an earlier saved module selection, not
                    necessarily the one ticked now. The bank is recounted by the server when this
                    selection is saved — publish to see an accurate count for it.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-gray-400">
                      {bankSize} question{bankSize === 1 ? '' : 's'} across the required modules,
                      drawing {track.questionCount} per attempt.
                    </p>
                    {!healthy && (
                      <p className="text-xs text-amber-700 mt-2">
                        Below {bankTarget} questions ({HEALTHY_BANK_MULTIPLE}&times; the {track.questionCount} drawn per
                        attempt), two attempts start to look alike. Add {shortBy} more question{shortBy === 1 ? '' : 's'} on
                        the Curriculum tab before publishing.
                      </p>
                    )}
                  </>
                )}
              </section>

              <section className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
                <label className="flex items-start justify-between gap-4 min-h-[44px]">
                  <span>
                    <span className="text-sm text-gray-900 font-medium">Activates partners</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Passing this certification switches a partner&apos;s referral link on. Only one
                      certification can do this — turning it on here turns it off everywhere else.
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    disabled={saving}
                    checked={track.activatesPartner === true}
                    onChange={(e) => setTrack({ ...track, activatesPartner: e.target.checked })}
                    className="w-5 h-5 accent-gray-900 mt-1 flex-shrink-0"
                  />
                </label>
              </section>

              {error && <p className="text-sm text-red-600">{error}</p>}
              {notice && <p className="text-sm text-gray-900 font-medium">{notice}</p>}

              <button
                type="button"
                onClick={publish}
                disabled={saving || track.moduleIds.length === 0}
                title={track.moduleIds.length === 0 ? 'Select at least one module before publishing.' : undefined}
                className="min-h-[44px] px-5 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Publishing…' : 'Publish exam'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
